import React, { useState } from 'react';

/**
 * ClimateModal — two taps, then what they mean for a routine.
 *
 * Opened only by /?open=climate, which is the CTA in the "The air is changing
 * your skin" email. No other entry point reaches it: without the param it never
 * mounts, and ChatUI strips the param once it has fired so a refresh or a
 * forwarded link cannot re-trigger it.
 *
 * The second step deliberately does not answer the question. Showing the advice
 * here satisfies the reason to sign in, so the step confirms what was captured
 * and offers the account as the way to keep it. Sofia's call: the goal of this
 * flow is a sign-in.
 *
 * Options are the ones PersonalProfile already uses, so an answer given here is
 * the same shape as one given in the profile screen and does not need mapping.
 */

const SANS = '"DM Sans", system-ui, sans-serif';
const SERIF = 'var(--font-sonmi-display)';

const CLIMATES = ['Humid', 'Dry', 'Temperate', 'Cold', 'Tropical'];
const WATER = ['Soft Water', 'Hard Water', 'Not Sure'];

// Direct opposites. Picking one clears the other, so nobody can tell Son:mi
// they are both tropical and cold. Deliberately only the contradictions:
// temperate with humid summers is a real place, so those stay combinable.
const CONFLICTS = { Humid: 'Dry', Dry: 'Humid', Cold: 'Tropical', Tropical: 'Cold' };

const Chip = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      fontFamily: SANS, fontSize: 15, padding: '11px 18px', borderRadius: 999,
      border: `1px solid ${selected ? '#341C1C' : '#E0D6CC'}`,
      background: selected ? '#341C1C' : '#FFFFFF',
      color: selected ? '#FFFFFF' : '#341C1C',
      cursor: 'pointer', transition: 'all .12s',
    }}
  >
    {label}
  </button>
);

export default function ClimateModal({ isOpen, isAnonymous, onSave, onClose, onCreateAccount, onSignIn }) {
  // Multi-select: plenty of places are two things at once, and someone with
  // humid summers and cold winters should be able to say so.
  const [climates, setClimates] = useState([]);
  const [water, setWater] = useState('');
  const [step, setStep] = useState('ask'); // 'ask' | 'result'

  if (!isOpen) return null;

  const toggle = (c) =>
    setClimates((prev) =>
      prev.includes(c)
        ? prev.filter((x) => x !== c)
        : [...prev.filter((x) => x !== CONFLICTS[c]), c]
    );


  const submit = () => {
    if (climates.length === 0) return;
    // Saved before the next step, so someone who closes the tab has still had
    // their answer recorded. onSave decides where it goes: the guest profile in
    // localStorage when anonymous, user_profiles when signed in.
    onSave?.({ climate: climates, waterType: water || '' });
    setStep('result');
  };

  const overlay = {
    position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(52,28,28,.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  };
  const card = {
    width: '100%', maxWidth: 460, maxHeight: '88vh', overflowY: 'auto',
    background: '#FFFFFF', borderRadius: 24, padding: '34px 30px 28px',
  };
  const h = { margin: 0, fontFamily: SERIF, fontSize: 30, lineHeight: 1.15, color: '#341C1C' };
  const label = {
    fontFamily: SANS, fontSize: 12, fontWeight: 500, letterSpacing: '.14em',
    textTransform: 'uppercase', color: '#B79B86', margin: '26px 0 12px',
  };
  // UpgradeAccountModal uses no uppercase anywhere, so the buttons that lead
  // into it should not either.
  const sentence = {
    width: '100%', marginTop: 26, padding: 16, borderRadius: 100, border: 0,
    background: '#341C1C', color: '#fff',
    fontFamily: SANS, fontSize: 16, fontWeight: 600, cursor: 'pointer',
  };
  // Guarded: this runs on every render, including the ask step where nothing is
  // selected yet, and climates[-1] would be undefined.
  const lower = climates.map((c) => c.toLowerCase());
  const listed =
    lower.length === 0 ? ''
    : lower.length === 1 ? lower[0]
    : `${lower.slice(0, -1).join(', ')} and ${lower[lower.length - 1]}`;
  const skip = {
    display: 'block', margin: '16px auto 0', background: 'none', border: 0,
    fontFamily: SANS, fontSize: 14, color: '#a99f97', cursor: 'pointer',
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        {step === 'ask' ? (
          <>
            <h2 style={h}>Where is your skin right now?</h2>
            <p style={{ fontFamily: SANS, fontSize: 15.5, lineHeight: 1.65, color: '#6f635c', margin: '12px 0 0' }}>
              Pick as many as apply. Son:mi will factor your weather into everything it suggests.
            </p>

            <p style={label}>Climate</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
              {CLIMATES.map((c) => (
                <Chip key={c} label={c} selected={climates.includes(c)} onClick={() => toggle(c)} />
              ))}
            </div>

            <p style={label}>Your water</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
              {WATER.map((w) => (
                <Chip key={w} label={w} selected={water === w} onClick={() => setWater(w)} />
              ))}
            </div>

            <button
              type="button"
              style={{ ...sentence, background: climates.length ? '#341C1C' : '#D8CCC0',
                       cursor: climates.length ? 'pointer' : 'not-allowed' }}
              onClick={submit}
              disabled={climates.length === 0}
            >
              Continue
            </button>
            <button type="button" style={skip} onClick={onClose}>Not now</button>
          </>
        ) : (
          <>
            <h2 style={h}>Got it.</h2>
            <p style={{ fontFamily: SANS, fontSize: 16, lineHeight: 1.65, color: '#341C1C', margin: '14px 0 0' }}>
              Son:mi has you down as <b>{listed}</b>{water && water !== 'Not Sure' ? <>, with <b>{water.toLowerCase()}</b></> : null}.
            </p>

            <p style={{
              fontFamily: SANS, fontSize: 15, lineHeight: 1.65, color: '#6f635c',
              margin: '20px 0 0', paddingTop: 18, borderTop: '1px solid #EFE7DF',
            }}>
              Sign in and this stays on your profile, so Son:mi factors it in every time. Without an account it only lasts on this device.
            </p>

            <button type="button" style={sentence} onClick={onSignIn}>Sign in to save this</button>
            <button type="button" style={{ ...sentence, background: '#FFFFFF', color: '#341C1C', border: '1px solid #341C1C', marginTop: 12 }} onClick={onClose}>
              Continue without an account
            </button>
          </>
        )}
      </div>
    </div>
  );
}
